import { useState, useEffect, useCallback } from 'react';
import friendsApi from '../services/friendsApi';

export const useFriends = () => {
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [friendSuggestions, setFriendSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFriends = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await friendsApi.getFriends();
      setFriends(response.friends || []);
    } catch (error) {
      console.error('Error fetching friends:', error);
      setError(error.message || 'Failed to load friends');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFriendRequests = useCallback(async () => {
    try {
      const response = await friendsApi.getFriendRequests();
      setFriendRequests(response.requests || []);
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  }, []);

  const fetchFriendSuggestions = useCallback(async () => {
    try {
      const response = await friendsApi.getFriendSuggestions();
      setFriendSuggestions(response.suggestions || []);
    } catch (error) {
      console.error('Error fetching friend suggestions:', error);
    }
  }, []);

  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await friendsApi.searchUsers(query);
      setSearchResults(response.users || []);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const sendFriendRequest = useCallback(async (userId) => {
    try {
      const response = await friendsApi.sendFriendRequest(userId);
      
      // Update search results to reflect sent request
      setSearchResults(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, hasPendingRequest: true }
          : user
      ));
      
      return response;
    } catch (error) {
      console.error('Error sending friend request:', error);
      throw error;
    }
  }, []);

  const acceptFriendRequest = useCallback(async (requestId) => {
    try {
      const response = await friendsApi.acceptFriendRequest(requestId);
      
      // Remove from requests and refresh friends list
      await Promise.all([
        fetchFriends(),
        fetchFriendRequests()
      ]);
      
      return response;
    } catch (error) {
      console.error('Error accepting friend request:', error);
      throw error;
    }
  }, [fetchFriends, fetchFriendRequests]);

  const declineFriendRequest = useCallback(async (requestId) => {
    try {
      const response = await friendsApi.declineFriendRequest(requestId);
      
      // Remove from requests list
      setFriendRequests(prev => prev.filter(request => request.id !== requestId));
      
      return response;
    } catch (error) {
      console.error('Error declining friend request:', error);
      throw error;
    }
  }, []);

  const removeFriend = useCallback(async (friendId) => {
    try {
      const response = await friendsApi.removeFriend(friendId);
      
      // Remove from friends list
      setFriends(prev => prev.filter(friend => 
        friend.user_id !== friendId && friend.id !== friendId
      ));
      
      return response;
    } catch (error) {
      console.error('Error removing friend:', error);
      throw error;
    }
  }, []);

  const getFriendActivity = useCallback(async (friendId) => {
    try {
      const response = await friendsApi.getFriendActivity(friendId);
      return response.activities || [];
    } catch (error) {
      console.error('Error fetching friend activity:', error);
      return [];
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchFriends(),
      fetchFriendRequests(),
      fetchFriendSuggestions()
    ]);
  }, [fetchFriends, fetchFriendRequests, fetchFriendSuggestions]);

  // Load initial data
  useEffect(() => {
    fetchFriends();
    fetchFriendRequests();
    fetchFriendSuggestions();
  }, [fetchFriends, fetchFriendRequests, fetchFriendSuggestions]);

  return {
    friends,
    friendRequests,
    searchResults,
    friendSuggestions,
    loading,
    searchLoading,
    error,
    searchUsers,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    removeFriend,
    getFriendActivity,
    refreshData,
    setError,
    clearSearchResults: () => setSearchResults([])
  };
};